import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/TiktokProfileAudience.graphql';
import { Query, QuerytiktokProfileAudienceArgs } from '../../__generated__/globalTypes';

export const useTiktokProfileAudienceQuery = (options?: QueryHookOptions<Query, QuerytiktokProfileAudienceArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerytiktokProfileAudienceArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useTiktokProfileAudienceLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerytiktokProfileAudienceArgs>
): QueryResult<Query, QuerytiktokProfileAudienceArgs> & {
  getTiktokProfileAudience: LazyQueryExecFunction<Query, QuerytiktokProfileAudienceArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getTiktokProfileAudience, result] = useLazyQuery<Query, QuerytiktokProfileAudienceArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getTiktokProfileAudience, ...result };
};

export const useTiktokProfileAudiencePromiseQuery = () => {
  const client = useApolloClient();

  const getTiktokProfileAudience = useCallback(
    (options?: Omit<QueryOptions<QuerytiktokProfileAudienceArgs, Query>, 'query'>) =>
      client.query<Query, QuerytiktokProfileAudienceArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getTiktokProfileAudience };
};
// Suspense Query Hooks
export const useTiktokProfileAudienceSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerytiktokProfileAudienceArgs>
) => {
  const result = useSuspenseQuery<Query, QuerytiktokProfileAudienceArgs>(QUERY, {
    ...options
  });

  return result;
};
